import { useCallback, useState, useEffect, useRef } from 'react'
import { DEFAULT_LANGUAGE } from './defaultContentLanguage'
import Styles from './LanguageInputField.module.scss'
import { ComboBox, ComboBoxProps } from 'components/ComboBox/ComboBox'
import { Item } from '@react-stately/collections'
import { useDefaultContentLanguage } from 'hooks/queries/useDefaultContentLanguage'
import cx from 'classnames'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { ChevronDown } from 'blaze-ui/icons'

export { DEFAULT_LANGUAGE } from './defaultContentLanguage'
export const SUPPORTED_LANGUAGES = [
  'Albanian',
  'Basque',
  'Belarusian',
  'Bosnian',
  'Brazilian Portuguese',
  'Bulgarian',
  'Catalan',
  'Croatian',
  'Czech',
  'Danish',
  'Dutch',
  'English',
  'English (UK)',
  'English (Australia)',
  'Estonian',
  'Faroese',
  'Finnish',
  'French',
  'Galician',
  'German',
  'Greek',
  'Hungarian',
  'Irish',
  'Italian',
  'Latvian',
  'Lithuanian',
  'Macedonian',
  'Maltese',
  'Moldovan',
  'Montenegrin',
  'Norwegian',
  'Polish',
  'Portuguese',
  'Romanian',
  'Russian',
  'Slovak',
  'Slovenian',
  'Spanish',
  'Swedish',
  'Ukrainian',
  'Welsh',
]

export const getDisplayLanguage = (internal: string) => (internal === 'English' ? 'English (US)' : internal)
export const getInternalLanguage = (display: string) => (display === 'English (US)' ? 'English' : display)

export const DISPLAY_LANGUAGES = SUPPORTED_LANGUAGES.map(getDisplayLanguage)

export type LanguageVariant = 'primary' | 'secondary'

interface Props {
  languageValue: string
  onLanguageChange: (language: string) => void
  variant?: LanguageVariant
  contentsClassName?: string
  wrapperClassName?: string
  popoverClassName?: string
  size?: ComboBoxProps['size']
  matchReferenceWidth?: boolean
}

export const LanguageInput = ({
  languageValue,
  onLanguageChange,
  contentsClassName,
  wrapperClassName,
  popoverClassName,
  variant = 'primary',
  size = 'lg',
  matchReferenceWidth = false,
}: Props) => {
  const defaultLanguage = useDefaultContentLanguage().data?.language
  const { isMobile } = useResponsiveDesign()
  const languageOptions = DISPLAY_LANGUAGES.map((languageOpt) => ({ id: languageOpt, value: languageOpt }))
  const [filteredLanguages, setFilteredLanguages] = useState(languageOptions)
  const [inputValue, setInputValue] = useState(getDisplayLanguage(languageValue))

  const resetSelectedKey = useCallback(() => {
    const displayValue = getDisplayLanguage(languageValue || defaultLanguage || DEFAULT_LANGUAGE)
    return languageOptions.find(({ id }) => id === displayValue)?.value || getDisplayLanguage(DEFAULT_LANGUAGE)
  }, [defaultLanguage, languageOptions, languageValue])

  const [selectedKey, setSelectedKey] = useState<string>(resetSelectedKey())

  const lastEmittedValue = useRef<string>(languageValue)

  const handleLanguageChange = useCallback(
    (newValue: string) => {
      if (newValue === lastEmittedValue.current) return

      lastEmittedValue.current = newValue
      onLanguageChange(newValue)
    },
    [onLanguageChange],
  )

  const findExactMatch = useCallback((value: string) => {
    return DISPLAY_LANGUAGES.find((language) => language.toLowerCase() === value.toLowerCase())
  }, [])

  const updateSelection = useCallback(
    (displayValue: string) => {
      handleLanguageChange(getInternalLanguage(displayValue))
      setSelectedKey(displayValue)
      setInputValue(displayValue)
    },
    [handleLanguageChange],
  )

  useEffect(() => {
    lastEmittedValue.current = languageValue
    setInputValue(getDisplayLanguage(languageValue))
  }, [languageValue])

  const handleInputChange = (value: string) => {
    setInputValue(value)
    setFilteredLanguages(languageOptions.filter(({ id }) => id.toLowerCase().includes(value.toLowerCase())))

    const exactMatch = findExactMatch(value)
    if (exactMatch) {
      handleLanguageChange(getInternalLanguage(exactMatch))
    }
  }

  const handleSelectionChange = (selection: string | null) => {
    if (!selection) return
    updateSelection(selection)
  }

  const handleBlur = () => {
    const exactMatch = findExactMatch(inputValue)

    if (exactMatch) {
      updateSelection(exactMatch)
    } else {
      const fallback = getDisplayLanguage(languageValue || DEFAULT_LANGUAGE)
      setInputValue(fallback)
      setSelectedKey(fallback)
    }
  }

  if (isMobile) {
    return (
      <div className={cx(Styles.mobileSelect, wrapperClassName, Styles[variant])}>
        <div className={Styles.mobileChevron}>
          <ChevronDown size={16} color="var(--dark-80)" />
        </div>
        <select value={selectedKey} name="language" onChange={(e) => handleSelectionChange(e.target.value)}>
          {languageOptions.map((l) => {
            return (
              <option key={l.id} value={l.id}>
                {l.value}
              </option>
            )
          })}
        </select>
      </div>
    )
  }

  return (
    <ComboBox
      wrapperClassName={cx(Styles.comboBoxField, wrapperClassName, Styles[variant])}
      popoverClassName={cx(Styles.comboBoxMenu, popoverClassName)}
      inputClassName={Styles.comboBoxInput}
      contentsClassName={cx(Styles.contents, contentsClassName)}
      popoverMaxHeight={180}
      menuTrigger="focus"
      aria-label="What language should the content be generated in?"
      placeholder={`${selectedKey} (default)`}
      data-testid="language-selector"
      size={size}
      items={filteredLanguages}
      inputValue={inputValue}
      selectedKey={selectedKey}
      onInputChange={handleInputChange}
      onSelectionChange={(selected) => handleSelectionChange(selected as string | null)}
      onBlur={handleBlur}
      chevronSize={16}
      isVirtualized={false}
      matchReferenceWidth={matchReferenceWidth}
    >
      {(item) => <Item>{item.value}</Item>}
    </ComboBox>
  )
}
